import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { PageHeader, Stat, StatusBadge, EmptyState } from "@/components/app/ui-bits";
import {
  Plus, Coins, Trash, PencilSimple, Paperclip, FileText,
  ArrowsClockwise, Wallet, ArrowUp, ArrowDown, Receipt, Bank, Warning,
  ChartLineUp, Buildings, Briefcase, ChartPieSlice, CurrencyDollar,
  Users, Money, CashRegister, Database, Stack, Bookmark, Calculator,
  CurrencyCircleDollar, ShieldCheck, Vault, BookOpenText,
  ClipboardText, PaperPlaneTilt,
  TreeStructure, CalendarBlank, Notebook, Scales, PresentationChart,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { can } from "@/lib/roles";
import { AssetRegisterTab, CashflowTab, BankReconTab, DonorTab } from "@/pages/finance/PhaseB";
import { InvoiceBuilderTab, BudgetTrackerTab, OverviewC } from "@/pages/finance/PhaseC";
import { ControlsTab } from "@/pages/finance/ControlsTab";
import AuditReportTab from "@/pages/finance/AuditReportTab";
import PayrollReview from "@/pages/finance/PayrollReviewTab";
import ARTab from "@/pages/finance/ARTab";
import APTab from "@/pages/finance/APTab";
import PettyCashTab from "@/pages/finance/PettyCashTab";
import { useUpload } from "@/pages/finance/_shared";
import FinanceHealthDashboard from "@/components/app/FinanceHealthDashboard";
import FinanceValidationWidget from "@/components/app/FinanceValidationWidget";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import FinancialAccountsTab from "@/pages/finance/FinancialAccountsTab";
import FinancePacksTab from "@/pages/finance/FinancePacksTab";
import GLChartOfAccountsTab from "@/pages/finance/GLChartOfAccountsTab";
import GLPeriodsTab from "@/pages/finance/GLPeriodsTab";
import GLJournalsTab from "@/pages/finance/GLJournalsTab";
import GLTrialBalanceTab from "@/pages/finance/GLTrialBalanceTab";
import GLStatementsTab from "@/pages/finance/GLStatementsTab";
import GLBankMatchTab from "@/pages/finance/GLBankMatchTab";
import FXCenterTab from "@/pages/finance/FXCenterTab";
import TaxCenterTab from "@/pages/finance/TaxCenterTab";
import ProjectAccountingTab from "@/pages/finance/ProjectAccountingTab";
import OpsMonitorTab from "@/pages/finance/OpsMonitorTab";
import ConsolidationTab from "@/pages/finance/ConsolidationTab";
import KpiDrillDialog from "@/pages/finance/KpiDrillDialog";
import ExportMenu from "@/components/app/ExportMenu";
import { GLPreview, usePostingMap } from "@/pages/finance/GLAccountBits";

// Finance tabs organised into functional groups — group pills on top, the
// active group's tabs below.
const TAB_GROUPS = [
  { id: "overview", l: "Overview", i: ChartPieSlice, tabs: [
    { v: "overview",      l: "Overview",            i: ChartPieSlice },
  ]},
  { id: "gl", l: "General Ledger", i: TreeStructure, tabs: [
    { v: "coa",           l: "Chart of Accounts",   i: TreeStructure },
    { v: "journals",      l: "Journals",            i: Notebook },
    { v: "trial_balance", l: "Trial Balance",       i: Scales },
    { v: "statements",    l: "Statements",          i: PresentationChart },
    { v: "periods",       l: "Periods",             i: CalendarBlank },
    { v: "bank_matching", l: "Bank Matching",       i: Bank },
  ]},
  { id: "receivables", l: "Receivables", i: Receipt, tabs: [
    { v: "ar",            l: "Receivables (AR)",    i: Receipt },
    { v: "invoices",      l: "Invoice Builder",     i: FileText },
    { v: "revenue",       l: "Revenue",             i: ChartLineUp },
  ]},
  { id: "payables", l: "Payables & Costs", i: Money, tabs: [
    { v: "ap",            l: "Payables (AP)",       i: Money },
    { v: "overhead",      l: "Overhead",            i: Buildings },
    { v: "project_cost",  l: "Project costs",       i: Briefcase },
    { v: "petty_cash",    l: "Petty Cash",          i: CashRegister },
    { v: "payroll",       l: "Payroll",             i: Users },
  ]},
  { id: "banking", l: "Assets & Banking", i: Bank, tabs: [
    { v: "assets",        l: "Asset Register",      i: Database },
    { v: "bank_recon",    l: "Bank Reconciliation", i: Bank },
    { v: "cashflow",      l: "Cashflow Forecast",   i: ArrowsClockwise },
    { v: "accounts",      l: "Accounts",            i: Vault },
  ]},
  { id: "planning", l: "Planning & Analysis", i: Calculator, tabs: [
    { v: "budgets",       l: "Budget Tracker",      i: Calculator },
    { v: "project_acct",  l: "Project Accounting",  i: Briefcase },
    { v: "donor",         l: "Donor Reporting",     i: Stack },
    { v: "consolidation", l: "Consolidation",       i: Scales },
  ]},
  { id: "taxctrl", l: "Tax & Controls", i: ShieldCheck, tabs: [
    { v: "tax",           l: "Tax Center",          i: CurrencyCircleDollar },
    { v: "fx",            l: "Multi-Currency",      i: CurrencyDollar },
    { v: "audit",         l: "Audit Report",        i: BookOpenText },
    { v: "controls",      l: "Controls",            i: ShieldCheck },
    { v: "ops",           l: "Ops Monitor",         i: ArrowsClockwise },
    { v: "packs",         l: "Finance Packs",       i: Bookmark },
  ]},
];

// ──────────────────────────────────────────────────────────────
// Two-level tab bar: group pills + the active group's tabs
function FinanceTabsBar({ tab, setTab }) {
  const group = TAB_GROUPS.find((g) => g.tabs.some((t) => t.v === tab)) || TAB_GROUPS[0];
  return (
    <div className="space-y-2" data-testid="finance-tabs-bar">
      <div className="flex flex-wrap gap-1.5">
        {TAB_GROUPS.map((g) => {
          const Icon = g.i;
          const active = g.id === group.id;
          return (
            <button
              key={g.id}
              type="button"
              onClick={() => setTab(g.tabs[0].v)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
                active
                  ? "bg-[#0F1E42] text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`}
              data-testid={`finance-group-${g.id}`}
            >
              <Icon size={13} weight={active ? "fill" : "regular"} />
              {g.l}
              {g.tabs.length > 1 && (
                <span className={`font-mono-alt text-[9px] ${active ? "text-blue-200" : "text-slate-400"}`}>
                  {g.tabs.length}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {group.tabs.length > 1 && (
        <div className="finance-tabs-bar">
          <div className="finance-tabs-scroll" style={{ padding: "2px 6px" }}>
            {group.tabs.map((t) => {
              const Icon = t.i;
              const active = tab === t.v;
              return (
                <button
                  key={t.v}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`finance-tab ${active ? "active" : ""}`}
                  onClick={() => setTab(t.v)}
                  data-testid={`tab-${t.v}`}
                >
                  {Icon ? <Icon size={15} weight={active ? "fill" : "regular"} /> : null}
                  <span>{t.l}</span>
                  <span className="finance-tab-underline" aria-hidden />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Entry form (Bug 2 + Section 2 enhancements)
function EntryForm({ initial, onSave, categories, projects, children, defaultType }) {
  const [open, setOpen] = useState(false);
  const BLANK = useMemo(() => ({
    entry_type: defaultType || "project_cost",
    category: "", subcategory: "", amount: 0,
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    project_id: "", description: "", vendor: "",
    cost_type: "direct", status: "recorded", currency: "USD",
    document_url: "", document_name: "",
  }), [defaultType]);
  const [form, setForm] = useState(initial || BLANK);
  const [busy, setBusy] = useState(false);
  const [docError, setDocError] = useState("");
  const fileRef = useRef(null);
  const { upload, busy: uploading } = useUpload();

  useEffect(() => { if (open) { setForm(initial || BLANK); setDocError(""); } }, [open, initial, BLANK]);

  const isCost = ["overhead", "project_cost"].includes(form.entry_type);
  const needsDoc = isCost && (form.amount || 0) > 200;
  const dueRequired = isCost;

  const submit = async (e) => {
    e.preventDefault();
    setDocError("");
    // Strict: project_cost MUST be linked to a project
    if (form.entry_type === "project_cost" && !form.project_id) {
      setDocError("A project link is required for project_cost entries.");
      return;
    }
    if (needsDoc && !form.document_url) {
      setDocError(`A supporting document is required for ${form.entry_type.replace("_", " ")} entries above $200.`);
      return;
    }
    if (dueRequired && !form.due_date) {
      setDocError("Due date is required for overhead and project-cost entries.");
      return;
    }
    setBusy(true);
    try { await onSave(form); setOpen(false); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const onFile = async (file) => {
    if (!file) return;
    try {
      const r = await upload(file);
      setForm({ ...form, document_url: r.url, document_name: r.name });
      toast.success("Document attached");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const catOpts = categories?.[form.entry_type] || [];

  // GL impact preview — mirrors the backend auto-posting rules exactly.
  const pmap = usePostingMap();
  const glRows = useMemo(() => {
    const amt = Math.abs(form.amount || 0);
    if (!pmap || amt <= 0) return [];
    const paid = ["paid", "posted"].includes(form.status);
    const cat = form.category;
    if (form.entry_type === "revenue") {
      return [{ code: "1120", debit: amt }, { code: pmap.revenue?.[cat] || "4900", credit: amt }];
    }
    const exp = form.entry_type === "overhead"
      ? (pmap.overhead?.[cat] || "6199")
      : (pmap.project_cost?.[cat] || "5199");
    return [{ code: exp, debit: amt }, { code: paid ? "1120" : "2140", credit: amt }];
  }, [pmap, form.amount, form.status, form.category, form.entry_type]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="finance-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{initial?.id ? "Edit entry" : "New finance entry"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-4 gap-4">
          <div className="col-span-2">
            <Label>Entry type *</Label>
            <Select value={form.entry_type} onValueChange={(v) => setForm({ ...form, entry_type: v, category: "" })}>
              <SelectTrigger data-testid="entry-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="overhead">Overhead (shared)</SelectItem>
                <SelectItem value="project_cost">Project Cost</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="entry-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>
                {catOpts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (USD) *</Label>
            <Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} data-testid="entry-amount-input" />
          </div>
          <div>
            <Label>Date *</Label>
            <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label>Due date {dueRequired && "*"}</Label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="entry-due-date-input" />
          </div>
          <div>
            <Label>Cost class {form.entry_type === "project_cost" && "*"}</Label>
            <Select value={form.cost_type} onValueChange={(v) => setForm({ ...form, cost_type: v })}>
              <SelectTrigger data-testid="entry-cost-class-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="direct">DIRECT — attributable to a specific project</SelectItem>
                <SelectItem value="indirect">INDIRECT — shared overhead (e.g. office rent, utilities)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(form.entry_type === "project_cost" || form.entry_type === "revenue") && (
            <div className="col-span-4">
              <Label>Project {form.entry_type === "project_cost" ? "*" : ""}</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="entry-project-select"><SelectValue placeholder="Search and pick a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} {p.code && `· ${p.code}`}</SelectItem>)}
                </SelectContent>
              </Select>
              {form.entry_type === "project_cost" && !form.project_id && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50/70 px-3 py-2 flex items-start gap-2" data-testid="entry-project-required-banner">
                  <Warning size={14} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-red-800">
                    <span className="font-semibold uppercase tracking-wider text-[10px]">Project link required</span>
                    <div className="mt-0.5">Every <span className="font-mono-alt">project_cost</span> entry must be linked to a project. This ensures finance reports, project budgets, and audit trails stay accurate. Pick a project above to continue.</div>
                  </div>
                </div>
              )}
              {form.entry_type === "revenue" && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Optional. Link this revenue to a project to feed the project financial snapshot (contract value, revenue booked, margin).
                </div>
              )}
            </div>
          )}
          <div className="col-span-2">
            <Label>Vendor / Source</Label>
            <Input value={form.vendor || ""} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recorded">Recorded</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="overdue">Overdue (auto)</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-4">
            <Label>Description</Label>
            <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          {glRows.length > 0 && (
            <div className="col-span-4">
              <GLPreview
                rows={glRows}
                note={["paid", "posted"].includes(form.status)
                  ? "Marked paid — settles straight from the bank account (1120)."
                  : form.entry_type === "revenue" ? undefined : "Not yet paid — accrues to 2140 until settled."}
                testid="entry-gl-preview"
              />
            </div>
          )}
          {/* Doc upload */}
          <div className="col-span-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Paperclip size={12} /> Supporting document {needsDoc && <span className="text-red-600">*</span>}
                </Label>
                <div className="text-[11px] text-slate-500">
                  {needsDoc
                    ? "Required for overhead/project-cost entries above $200 (invoice or receipt)."
                    : "Optional — invoice, receipt, or any backup."}
                </div>
                {form.document_name && (
                  <div className="mt-1 inline-flex items-center gap-1 text-xs text-slate-700">
                    <FileText size={12} /> {form.document_name}
                  </div>
                )}
              </div>
              <Input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="entry-doc-input" />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="entry-doc-attach-btn">
                {uploading ? "Uploading…" : form.document_url ? "Replace" : "Attach"}
              </Button>
            </div>
          </div>
          {docError && (
            <div className="col-span-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="entry-doc-error">{docError}</div>
          )}
          <DialogFooter className="col-span-4">
            <Button type="submit" disabled={busy} className="btn-primary-blue" data-testid="entry-save-button">{busy ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Net profit modal (Bug 4)
function NetProfitModal({ open, onOpenChange, summary }) {
  if (!summary) return null;
  const margin = summary.total_revenue ? (summary.net / summary.total_revenue) * 100 : 0;
  const Row = ({ label, value, sign, bold }) => (
    <div className={`flex items-center justify-between py-2 ${bold ? "border-t border-slate-200 pt-3 font-display text-lg font-bold" : "text-sm"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{sign}{formatCurrency(value)}</span>
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="net-profit-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Net profit breakdown</DialogTitle>
          <DialogDescription>Live numbers from your finance entries.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Row label="Revenue" value={summary.total_revenue} sign="+" />
          <Row label="Project costs" value={summary.total_project_cost} sign="−" />
          <Row label="Overhead" value={summary.total_overhead} sign="−" />
          <Row label="Net" value={summary.net} sign={summary.net >= 0 ? "" : "−"} bold />
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>Net margin</span>
            <span className="tabular-nums">{margin.toFixed(1)}%</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────
// Main page
export default function Finance() {
  const { user } = useAuth();
  const canEdit = can(user, "finance.entries_create") ?? hasMinRole(user, "manager");
  const canDelete = can(user, "finance.entries_delete") ?? hasMinRole(user, "admin");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [entries, setEntries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [overview, setOverview] = useState(null);
  const [categories, setCategories] = useState({});
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [netModalOpen, setNetModalOpen] = useState(false);
  const [drillKpi, setDrillKpi] = useState(null);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab && tab !== "overview") next.set("tab", tab); else next.delete("tab");
      return next;
    }, { replace: true });
  }, [tab, setSearchParams]);

  const load = async () => {
    const [e, s, c, p, h, ov] = await Promise.all([
      api.get("/finance/entries"),
      api.get("/finance/summary"),
      api.get("/finance/categories"),
      api.get("/projects"),
      api.get("/hr/staff").catch(() => ({ data: [] })),
      api.get("/finance/overview").catch(() => ({ data: null })),
    ]);
    setEntries(e.data);
    setSummary(s.data);
    setOverview(ov.data);
    setCategories(c.data);
    setProjects(p.data);
    setStaff(Array.isArray(h.data) ? h.data : (h.data?.items || []));
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const staffName = (id) => staff.find((s) => s.id === id)?.full_name || id;  // Bug 1 — UUID → name

  const filtered = useMemo(() => {
    const list = entries.map((e) => {
      // Bug 3 client-side overdue check
      if (e.status !== "paid" && e.status !== "overdue" && e.due_date) {
        const due = new Date(e.due_date).getTime();
        if (due < Date.now()) return { ...e, status: "overdue" };
      }
      return e;
    });
    if (tab === "overview") return list;
    if (["overhead", "project_cost", "revenue"].includes(tab)) return list.filter((x) => x.entry_type === tab);
    return list;
  }, [entries, tab]);

  const save = async (form) => {
    if (form.id) {
      await api.patch(`/finance/entries/${form.id}`, form);
      toast.success("Entry updated");
    } else {
      await api.post("/finance/entries", form);
      toast.success("Entry recorded");
    }
    load();
  };

  const remove = async (id) => {
    try { await api.delete(`/finance/entries/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const requestApproval = async (id) => {
    try {
      await api.post(`/finance/entries/${id}/request-approval`);
      toast.success("Approval requested · routed to CEO");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const showAddBtn = ["overview", "overhead", "project_cost", "revenue"].includes(tab);
  const isPhaseBTab = ["assets", "cashflow", "bank_recon", "donor", "ar", "ap", "petty_cash", "payroll", "invoices", "budgets", "tax", "fx", "audit", "controls", "packs"].includes(tab);

  // Type-specific export wiring — each subsection downloads its own structured report
  const ledgerTypes = new Set(["overview", "revenue", "overhead", "project_cost"]);
  let exportModule = "finance-ledger";
  let exportFilters = {};
  if (tab === "petty_cash") {
    exportModule = "finance-petty-cash";
  } else if (tab === "cashflow") {
    exportModule = "finance-cashflow";
  } else if (tab === "assets") {
    exportModule = "finance-assets";
  } else if (tab === "invoices" || tab === "ar") {
    exportModule = "finance-invoices";
  } else if (tab === "budgets") {
    exportModule = "finance-budgets";
  } else if (tab === "payroll") {
    exportModule = "finance-payroll";
  } else if (tab === "tax") {
    exportModule = "finance-tax";
  } else if (tab === "donor") {
    exportModule = "finance-donor";
  } else if (tab === "project_cost") {
    exportModule = "finance-project-costs";
  } else if (tab === "bank_recon") {
    exportModule = "finance-bank-recon";
  } else if (ledgerTypes.has(tab)) {
    exportModule = "finance-ledger";
    exportFilters = { entry_type: tab === "overview" ? undefined : tab };
  } else {
    // Remaining specialty tabs (ap, fx, audit, controls, accounts, packs) — ledger fallback
    exportModule = "finance-ledger";
  }

  return (
    <div data-testid="finance-page">
      <PageHeader
        eyebrow="Accounting"
        title="Finance"
        description="Revenue, costs, payroll, receivables, payables and petty cash — all in one place."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ExportMenu module={exportModule} filters={exportFilters} />
            {canEdit && showAddBtn && (
              <EntryForm onSave={save} categories={categories} projects={projects} defaultType={tab === "overview" ? "project_cost" : tab}>
                <Button className="btn-primary-blue" data-testid="add-entry-button"><Plus size={14} className="mr-2" /> New entry</Button>
              </EntryForm>
            )}
          </div>
        }
      />
      <div className="p-4 md:p-8 space-y-6">
        {/* Top KPI cards (Bug 4 — clickable) */}
        {summary && (
          <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[
              { kpi: "revenue", tab: "revenue", label: "Total Revenue", value: summary.total_revenue, accent: true },
              { kpi: "project_cost", tab: "project_cost", label: "Project Costs", value: summary.total_project_cost },
              { kpi: "overhead", tab: "overhead", label: "Overhead", value: summary.total_overhead },
            ].map((c) => (
              <div key={c.kpi} className="relative">
                <button onClick={() => setTab(c.tab)} className="w-full text-left" data-testid={`kpi-${c.kpi.replace("_", "-")}`}>
                  <Stat label={c.label} value={formatCurrency(c.value)} sub="Click to view entries" accent={c.accent} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDrillKpi(c.kpi); }}
                  className="absolute right-2 top-2 rounded-full bg-slate-100 px-2 py-0.5 font-mono-alt text-[9px] font-bold text-slate-500 hover:bg-[#1E5CBA] hover:text-white transition-colors"
                  data-testid={`drill-${c.kpi}`}
                  title="GL drill-down"
                >
                  GL ⌕
                </button>
              </div>
            ))}
            <div className="relative">
              <button onClick={() => setNetModalOpen(true)} className="w-full text-left" data-testid="kpi-net">
                <Stat
                  label="Net"
                  value={formatCurrency(summary.net)}
                  sub={summary.net >= 0 ? "Click for breakdown" : "Loss · click for breakdown"}
                />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDrillKpi("net"); }}
                className="absolute right-2 top-2 rounded-full bg-slate-100 px-2 py-0.5 font-mono-alt text-[9px] font-bold text-slate-500 hover:bg-[#1E5CBA] hover:text-white transition-colors"
                data-testid="drill-net"
                title="Dashboard ↔ Ledger reconciliation"
              >
                GL ⌕
              </button>
            </div>
          </section>
        )}
        <KpiDrillDialog kpi={drillKpi} onClose={() => setDrillKpi(null)} />

        {/* Tab strip */}
        <FinanceTabsBar tab={tab} setTab={setTab} />

        {/* Tab body */}
        {tab === "payroll" ? (
          <PayrollReview canEdit={canEdit} userEmail={user?.email} staffName={staffName} />
        ) : tab === "coa" ? (
          <GLChartOfAccountsTab canEdit={canEdit} isAdmin={canDelete} />
        ) : tab === "periods" ? (
          <GLPeriodsTab isAdmin={canDelete} />
        ) : tab === "journals" ? (
          <GLJournalsTab canEdit={canEdit} isAdmin={canDelete} userEmail={user?.email} />
        ) : tab === "trial_balance" ? (
          <GLTrialBalanceTab />
        ) : tab === "statements" ? (
          <GLStatementsTab />
        ) : tab === "project_acct" ? (
          <ProjectAccountingTab />
        ) : tab === "bank_matching" ? (
          <GLBankMatchTab canEdit={canEdit} />
        ) : tab === "ar" ? (
          <ARTab canEdit={canEdit} projects={projects} />
        ) : tab === "ap" ? (
          <APTab canEdit={canEdit} projects={projects} />
        ) : tab === "petty_cash" ? (
          <PettyCashTab canEdit={canEdit} />
        ) : tab === "assets" ? (
          <AssetRegisterTab canEdit={canEdit} />
        ) : tab === "cashflow" ? (
          <CashflowTab canEdit={canEdit} />
        ) : tab === "bank_recon" ? (
          <BankReconTab canEdit={canEdit} isCeo={user?.email === "ceo@descon.group" || user?.role === "super_admin"} />
        ) : tab === "donor" ? (
          <DonorTab canEdit={canEdit} />
        ) : tab === "invoices" ? (
          <InvoiceBuilderTab canEdit={canEdit} projects={projects} />
        ) : tab === "budgets" ? (
          <BudgetTrackerTab canEdit={canEdit} />
        ) : tab === "tax" ? (
          <TaxCenterTab canEdit={canEdit} />
        ) : tab === "fx" ? (
          <FXCenterTab canEdit={canEdit} />
        ) : tab === "audit" ? (
          <AuditReportTab canEdit={canEdit} />
        ) : tab === "controls" ? (
          <ControlsTab isAdmin={canDelete} canEdit={canEdit} />
        ) : tab === "ops" ? (
          <OpsMonitorTab canEdit={canEdit} />
        ) : tab === "consolidation" ? (
          <ConsolidationTab canEdit={canEdit} isSuperAdmin={user?.role === "super_admin"} />
        ) : tab === "accounts" ? (
          <FinancialAccountsTab canEdit={canEdit} />
        ) : tab === "packs" ? (
          <FinancePacksTab />
        ) : tab === "overview" ? (
          <div className="space-y-5">
            <FinanceHealthDashboard onJumpToTab={setTab} />
            <FinanceValidationWidget />
            <OverviewC onTabChange={setTab} />
          </div>
        ) : loading ? (
          <div className="font-mono-alt text-xs uppercase tracking-[0.25em] text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Coins} title="No finance entries" description="Record overhead, project costs and revenue to track your spend." />
        ) : (
          <div className="dc-card responsive-table-wrap">
            <table className="data-table w-full min-w-[1100px]">
              <thead>
                <tr>
                  <th>Date</th><th>Due</th><th>Type</th><th>Category</th><th>Project / Vendor</th>
                  <th>Cost class</th><th className="text-right">Amount</th><th>Status</th><th>Doc</th>
                  {canEdit && <th className="w-32">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => {
                  const isCost = ["overhead", "project_cost"].includes(e.entry_type);
                  const canRequestApproval = isCost && (e.amount || 0) > 500 && ["recorded", "pending"].includes(e.status);
                  return (
                    <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                      <td className="font-mono-alt text-xs tabular-nums">{formatDate(e.date)}</td>
                      <td className="font-mono-alt text-xs tabular-nums">{e.due_date ? formatDate(e.due_date) : "—"}</td>
                      <td className="text-xs uppercase tracking-wider">{e.entry_type?.replace("_", " ")}</td>
                      <td className="font-semibold text-slate-900">{e.category}{e.subcategory && <span className="text-xs text-slate-500"> · {e.subcategory}</span>}</td>
                      <td className="text-xs text-slate-700">{e.project_id ? projectName(e.project_id) : (e.vendor || "—")}</td>
                      <td className="text-xs uppercase tracking-wider text-slate-600">{e.cost_type}</td>
                      <td className="text-right font-mono-alt tabular-nums">{formatCurrency(e.amount)}</td>
                      <td><StatusBadge status={e.status} /></td>
                      <td>
                        {e.document_url
                          ? <a href={e.document_url} target="_blank" rel="noreferrer" className="text-[#1E5CBA] hover:underline text-xs"><FileText size={12} className="inline" /></a>
                          : <span className="text-xs text-slate-400">—</span>}
                      </td>
                      {canEdit && (
                        <td>
                          <div className="flex items-center gap-1">
                            <AuditTrailDrawer entity="finance_entry" entityId={e.id} label={`Entry · ${e.category || "—"} · ${formatCurrency(e.amount)}`} trigger={
                              <Button variant="outline" size="sm" className="h-8" title="Audit trail" data-testid={`audit-entry-${e.id}`}><ClipboardText size={12} /></Button>
                            } />
                            {canRequestApproval && (
                              <Button size="sm" variant="outline" className="h-8" title="Request CEO approval" onClick={() => requestApproval(e.id)} data-testid={`request-approval-${e.id}`}>
                                <PaperPlaneTilt size={12} />
                              </Button>
                            )}
                            <EntryForm initial={e} onSave={(f) => save({ ...f, id: e.id })} categories={categories} projects={projects}>
                              <Button variant="outline" size="sm" className="h-8" data-testid={`edit-entry-${e.id}`}><PencilSimple size={12} /></Button>
                            </EntryForm>
                            {canDelete && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="h-8" data-testid={`delete-entry-${e.id}`}><Trash size={12} /></Button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Delete entry?</AlertDialogTitle></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(e.id)}>Delete</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <NetProfitModal open={netModalOpen} onOpenChange={setNetModalOpen} summary={summary} />
    </div>
  );
}

